const article = {
  slug: "how-to-relist-a-house",
  cluster: "seller",
  title: "How to Relist a House That Didn't Sell (Reset, Reprice, Repromote)",
  description:
    "How to relist a house that didn't sell: a reset, reprice, repromote framework. When to relist vs wait, why you refresh photos, description, and price together, plus a relist checklist.",
  audience: "Sellers & agents",
  funnelStage: "MOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "8 min read",
  tldr: "To relist a house that didn't sell, do a clean reset: change all three levers at once — refresh the photos, rewrite the description, and reprice to current sold comps — then relaunch as if it were brand new. Fixing one at a time wastes your best asset, the relaunch moment, and trains buyers to keep waiting for the next cut. Relist (don't just wait) once views, showings, or days on market show the listing has gone stale.",
  blocks: [
    {
      type: "p",
      spans: [
        "Relisting a house that didn't sell works best as a single, decisive reset — not a slow drip of tweaks. The homes that come back and sell almost always change all three levers at once: **fresh photos**, a **rewritten description**, and a **price that matches recent sold comps**, relaunched together as if the listing were brand new. Doing them one at a time burns your most valuable asset (the relaunch moment) and quietly teaches buyers to wait for the next price cut.",
      ],
    },
    {
      type: "p",
      spans: [
        "This is the reset / reprice / repromote framework: why all three move together, how to decide whether to relist or wait, and exactly what to do before the listing goes live again.",
      ],
    },
    {
      type: "h2",
      text: "Should you relist or just wait?",
      id: "relist-vs-wait",
    },
    {
      type: "p",
      spans: [
        "Waiting only helps if the market is moving toward your price — more buyers arriving, comparable homes selling above where yours is listed. In almost every other case, sitting still makes things worse, because a high ",
        {
          text: "days-on-market",
          href: "/resources/days-on-market-explained",
        },
        " count becomes its own problem: buyers assume something is wrong and either skip the listing or lowball it. Relist when the listing has clearly gone stale; wait only when there's a concrete, external reason to expect demand to rise.",
      ],
    },
    {
      type: "table",
      headers: ["What you're seeing", "Relist or wait?", "Why"],
      rows: [
        [
          "Lots of online views, almost no showings",
          "Relist (reprice + refresh)",
          "The price or lead photo is filtering buyers out before they book",
        ],
        [
          "Showings but no offers",
          "Relist (presentation + price expectations)",
          "Condition, layout, or price isn't matching what buyers see in person",
        ],
        [
          "Few views at all",
          "Relist (repromote + new lead photo)",
          "Exposure or the first photo is the bottleneck",
        ],
        [
          "Strong start, then it went quiet",
          "Relist (full reset)",
          "It's gone stale and is now competing as an old listing",
        ],
        [
          "Comparable homes just started selling above your price",
          "Wait briefly, then reassess",
          "The market may be moving toward you",
        ],
      ],
    },
    {
      type: "callout",
      spans: [
        "Rule of thumb: if your home gets plenty of online views but very few showings, waiting rarely fixes it. That pattern points to price — and the cure is a reset, not patience.",
      ],
    },
    {
      type: "h2",
      text: "Why refresh photos, description, and price together — not one at a time?",
      id: "why-all-three-together",
    },
    {
      type: "p",
      spans: [
        "This is the single most common relisting mistake: changing one thing, waiting two weeks, changing the next, waiting again. It feels careful. It's actually the slowest, weakest way to relaunch, for three reasons.",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "**You only get one relaunch spike.** When a listing comes back fresh, it briefly surfaces again in buyer searches and alerts. Spend that moment on a listing that's fully fixed — right price, best photos, sharp description — not on a half-update that wastes the attention.",
        ],
        [
          "**Drip price cuts train buyers to wait.** A series of small reductions signals there's more to come, so buyers sit on their hands expecting the next one. One decisive correction to within the range of recent sold comps reads as a real opportunity, not a slow-motion negotiation.",
        ],
        [
          "**The levers reinforce each other.** A great new photo can't rescue an over-market price; a fair price still gets skipped behind a dark, cluttered lead image. Buyers judge price *and* presentation in the same glance, so they have to be right at the same time.",
        ],
      ],
    },
    {
      type: "h2",
      text: "Step 1: Reset the presentation",
      id: "the-reset",
    },
    {
      type: "p",
      spans: [
        "Treat the relisted home as a new product, because to most buyers it is. Re-shoot or re-sequence the photos so the strongest room leads and the sequence walks buyers through the home in a logical order — see ",
        {
          text: "the best order for listing photos",
          href: "/resources/best-order-for-listing-photos",
        },
        ". Replace any dark, gray-day, or cluttered shots; declutter and depersonalize the rooms that drew the weakest feedback the first time around.",
      ],
    },
    {
      type: "p",
      spans: [
        "Then rewrite the description from scratch rather than editing the old one. Lead with what's genuinely special about the home and the lifestyle it offers, not a list of facts the photos already show. Our guide on ",
        {
          text: "how to write a listing description that sells",
          href: "/resources/how-to-write-a-listing-description-that-sells",
        },
        " breaks down the structure. When you describe the area, stay strictly factual and use-based — walkability, commute times, nearby amenities — never anything about who lives there.",
      ],
    },
    {
      type: "h2",
      text: "Step 2: Reprice to the current market",
      id: "the-reprice",
    },
    {
      type: "p",
      spans: [
        "Reprice against what comparable homes actually **sold** for in the last few months — not what they're currently listed for, and not what you originally hoped to get. The first listing's price was tested by the market and lost; the relist price has to clearly win. A quick ",
        {
          text: "comparative market analysis",
          href: "/resources/what-is-a-cma",
        },
        " tells you where you genuinely stand, and ",
        {
          text: "how to price a listing from a CMA",
          href: "/resources/how-to-price-a-listing-cma",
        },
        " shows how to turn those comps into a number.",
      ],
    },
    {
      type: "ul",
      items: [
        ["Anchor to recent **sold** comps, adjusting honestly for condition, size, and location."],
        [
          "Make one clear, meaningful correction instead of several small ones that keep trailing the market.",
        ],
        [
          "Mind search-bucket thresholds — pricing at $515,000 hides the home from every buyer who caps their search at $500,000.",
        ],
        [
          "If ",
          {
            text: "the home was overpriced",
            href: "/resources/is-my-house-overpriced",
          },
          " the first time, the relist is your chance to fix it decisively, not to inch toward it.",
        ],
      ],
    },
    {
      type: "h2",
      text: "Step 3: Repromote like a fresh launch",
      id: "the-repromote",
    },
    {
      type: "p",
      spans: [
        "With the fixes done, relaunch deliberately. Confirm the listing is syndicated everywhere buyers actually look, and time the go-live so it lands ahead of the weekend, when most buyers shop. Add the pushes the first launch may have skipped: an open house, a social post, and a note to local buyer agents announcing the improved home and price.",
      ],
    },
    {
      type: "callout",
      spans: [
        "Sequence that works: finish all the fixes first, then relaunch in one moment with a new price, new photos, and a new description live together. Never relaunch a listing that's still half-fixed.",
      ],
    },
    {
      type: "h2",
      text: "A quick relist checklist",
      id: "relist-checklist",
    },
    {
      type: "ol",
      items: [
        [
          "Confirm it's a reset situation: check your view-to-showing ratio, days on market, and recent buyer feedback.",
        ],
        ["Re-shoot or re-sequence photos; replace every weak image and lead with the best room."],
        [
          "Rewrite the description from scratch, leading with what's special and staying strictly factual about the area.",
        ],
        ["Reprice to recent sold comps in one decisive correction."],
        ["Stage the relaunch: syndication, weekend timing, open house, and outreach."],
        [
          "Go live with all three changes at once — and have an objective artifact ready to back up the new price.",
        ],
      ],
    },
    {
      type: "h2",
      text: "How do you prove the relisted price is right?",
      id: "credibility-artifact",
    },
    {
      type: "p",
      spans: [
        "After a home sits unsold, sellers (and buyers) are skeptical — so the relist needs evidence, not just a new number. A ",
        {
          text: "Listino review",
          href: "/get-started",
        },
        " gives you that artifact in one pass: it checks your price position against comps, rewrites the description, re-sequences and touches up the photos, and includes a comparative market analysis. For agents, it's a credible, third-party way to walk a hesitant seller through the reset; for sellers, it's the confidence that the new price and presentation are actually right this time — so you relaunch once and relaunch correctly.",
      ],
    },
  ],
  faqs: [
    {
      question: "How long should I wait before relisting a house that didn't sell?",
      answer: [
        {
          type: "p",
          spans: [
            "There's no fixed number — measure it against your local market. Once a listing passes the typical time-to-offer for your area and the signals point to a stale listing (lots of views but few showings, or a strong start that went quiet), that's the moment to relist, not to keep waiting. Waiting only helps when there's a concrete reason to expect demand to rise, such as comparable homes starting to sell above your price.",
          ],
        },
      ],
    },
    {
      question: "Does relisting reset the days-on-market count?",
      answer: [
        {
          type: "p",
          spans: [
            "It depends on the platform and how the listing is taken off and put back on the market; some sites reset the visible days-on-market counter and some carry over or show listing history. Don't relist purely to reset a number — buyers and their agents can often see prior history anyway. Relist because you've genuinely fixed the price and presentation, so the fresh launch is backed by real changes, not just a reset clock.",
          ],
        },
      ],
    },
    {
      question: "Should I change the price, the photos, or the description first?",
      answer: [
        {
          type: "p",
          spans: [
            "None of them first — change all three at the same time. A relisted home gets one relaunch spike of renewed buyer attention, and buyers judge price and presentation together in a single glance. Fixing one lever at a time wastes that moment and, in the case of repeated small price cuts, trains buyers to wait for the next reduction. Finish every fix, then relaunch them together.",
          ],
        },
      ],
    },
    {
      question: "Will better photos help if the house was overpriced?",
      answer: [
        {
          type: "p",
          spans: [
            "Better photos won't rescue an over-market price — buyers compare listings side by side and skip the one that's priced wrong, no matter how good it looks. Photos and price work together: a fair price still gets passed over behind a weak lead image, and a great photo can't justify a number above recent sold comps. On a relist, fix the price and the presentation at the same time.",
          ],
        },
      ],
    },
    {
      question: "Can I relist the house myself, or do I need an agent?",
      answer: [
        {
          type: "p",
          spans: [
            "You can run the reset / reprice / repromote process whether you're an agent, a seller working with one, or selling on your own. The mechanics are the same: reset the presentation, reprice to recent sold comps, and repromote as a fresh launch. What matters most is having objective evidence behind the new price and a clean, all-at-once relaunch — a structured listing review gives you both regardless of who's listing the home.",
          ],
        },
      ],
    },
  ],
  related: [
    "days-on-market-explained",
    "best-order-for-listing-photos",
    "how-to-write-a-listing-description-that-sells",
    "what-is-a-cma",
  ],
};

export default article;
